/**
 * Hand History Log - Scrollable history of all hands played by both players
 * with click-to-expand score breakdown details
 */

import React, { useState, useMemo } from 'react';
import { TurnResolution, ScoreBreakdown } from '@solante/shared';
import { motion, AnimatePresence } from 'framer-motion';
import { shortKey } from '../../../utils/format';

interface HandHistoryLogProps {
  history: TurnResolution[];
  myWallet?: string;
}

// Suit emoji mapping
const suitEmoji: Record<string, string> = {
  Stake: '★',
  Sol: '☀',
  Burn: '✦',
  Mint: '≋',
};

// Detailed score breakdown component
const DetailedBreakdown: React.FC<{ breakdown: ScoreBreakdown }> = ({ breakdown }) => {
  const baseScore = breakdown.combo.score;
  const multiplier = breakdown.combo.multiplier;
  const bonusModifiers = breakdown.modifiers.filter((m) => m.bonus > 0);
  const penaltyModifiers = breakdown.modifiers.filter((m) => m.bonus < 0);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-2 pt-2 border-t border-white/10 text-xs"
    >
      {/* Base calculation */}
      <div className="flex items-center justify-between text-white/60 mb-1">
        <span>{breakdown.combo.name} Base</span>
        <span className="font-mono">{baseScore}</span>
      </div>

      {multiplier > 1 && (
        <div className="flex items-center justify-between text-amber-400/80 mb-1">
          <span>Combo Multiplier</span>
          <span className="font-mono">x{multiplier}</span>
        </div>
      )}

      {/* Bonus modifiers */}
      {bonusModifiers.map((mod, i) => (
        <div
          key={`bonus-${i}`}
          className="flex items-center justify-between text-emerald-400/80 mb-1"
        >
          <span>{mod.name}</span>
          <span className="font-mono">+{mod.bonus}</span>
        </div>
      ))}

      {/* Penalty modifiers */}
      {penaltyModifiers.map((mod, i) => (
        <div
          key={`penalty-${i}`}
          className="flex items-center justify-between text-rose-400/80 mb-1"
        >
          <span>{mod.name}</span>
          <span className="font-mono">{mod.bonus}</span>
        </div>
      ))}

      {/* Total */}
      <div className="flex items-center justify-between font-semibold text-neon-400 pt-1 border-t border-white/5">
        <span>Total</span>
        <span className="font-mono">{breakdown.total}</span>
      </div>
    </motion.div>
  );
};

// Single hand entry component
const HandEntry: React.FC<{
  turn: TurnResolution;
  isMyTurn: boolean;
  runningScores: { me: number; opponent: number };
}> = ({ turn, isMyTurn, runningScores }) => {
  const [expanded, setExpanded] = useState(false);

  const playerColor = isMyTurn
    ? 'border-neon-400/30 bg-neon-500/5'
    : 'border-red-400/30 bg-red-500/5';
  const playerTextColor = isMyTurn ? 'text-neon-400' : 'text-red-400';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-lg border ${playerColor} p-2 cursor-pointer transition hover:bg-white/5`}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/40">#{turn.sequence + 1}</span>
          <span className={`text-xs font-semibold ${playerTextColor}`}>
            {isMyTurn ? 'You' : shortKey(turn.player)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Running score */}
          <span className="text-[10px] text-white/40">
            {runningScores.me} - {runningScores.opponent}
          </span>
          <span className={`font-bold text-sm ${playerTextColor}`}>+{turn.breakdown.total}</span>
        </div>
      </div>

      {/* Cards played */}
      <div className="flex items-center gap-1 mt-1">
        <span className="text-[10px] text-white/40 mr-1">Hand:</span>
        <div className="flex gap-1">
          {turn.cardsPlayed.map((card, i) => (
            <span key={i} className="px-1.5 py-0.5 rounded bg-white/10 text-[11px] text-white/80">
              {card.rank}
              {suitEmoji[card.suit] ?? ''}
            </span>
          ))}
        </div>
        <span className="ml-auto text-[10px] text-white/50">{turn.breakdown.combo.name}</span>
      </div>

      {/* Expand indicator */}
      <div className="flex justify-center mt-1">
        <span className="text-[10px] text-white/30">{expanded ? '▲ Less' : '▼ Details'}</span>
      </div>

      {/* Expanded breakdown */}
      <AnimatePresence>
        {expanded && <DetailedBreakdown breakdown={turn.breakdown} />}
      </AnimatePresence>
    </motion.div>
  );
};

type FilterType = 'all' | 'me' | 'opponent';

export const HandHistoryLog: React.FC<HandHistoryLogProps> = ({ history, myWallet }) => {
  const [filter, setFilter] = useState<FilterType>('all');

  // Calculate running scores for each turn
  const turnsWithRunningScores = useMemo(() => {
    let myScore = 0;
    let opponentScore = 0;

    return history.map((turn) => {
      const isMyTurn = myWallet ? turn.player === myWallet : false;
      if (isMyTurn) {
        myScore += turn.breakdown.total;
      } else {
        opponentScore += turn.breakdown.total;
      }
      return {
        turn,
        isMyTurn,
        runningScores: { me: myScore, opponent: opponentScore },
      };
    });
  }, [history, myWallet]);

  // Filter turns based on selection
  const filteredTurns = useMemo(() => {
    if (filter === 'all') return turnsWithRunningScores;
    if (filter === 'me') return turnsWithRunningScores.filter((t) => t.isMyTurn);
    return turnsWithRunningScores.filter((t) => !t.isMyTurn);
  }, [turnsWithRunningScores, filter]);

  // Calculate totals for summary
  const totals = useMemo(() => {
    const finalTurn = turnsWithRunningScores[turnsWithRunningScores.length - 1];
    return finalTurn?.runningScores ?? { me: 0, opponent: 0 };
  }, [turnsWithRunningScores]);

  if (history.length === 0) {
    return (
      <div className="arcade-panel p-3 text-center">
        <span className="text-xs text-white/50">No hands played yet</span>
      </div>
    );
  }

  return (
    <div className="arcade-panel p-2 flex flex-col h-full">
      {/* Header with totals */}
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[10px] uppercase tracking-wide text-white/50">Hand History</span>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-neon-400 font-semibold">{totals.me}</span>
          <span className="text-white/30">-</span>
          <span className="text-red-400 font-semibold">{totals.opponent}</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-2">
        {(['all', 'me', 'opponent'] as FilterType[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`flex-1 px-2 py-1 rounded text-[10px] uppercase tracking-wide transition ${
              filter === f ? 'bg-white/20 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
            }`}
          >
            {f === 'all' ? `All (${history.length})` : f === 'me' ? 'My Hands' : 'Opponent'}
          </button>
        ))}
      </div>

      {/* Scrollable history */}
      <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0">
        <AnimatePresence mode="popLayout">
          {filteredTurns.map(({ turn, isMyTurn, runningScores }) => (
            <HandEntry
              key={turn.sequence}
              turn={turn}
              isMyTurn={isMyTurn}
              runningScores={runningScores}
            />
          ))}
        </AnimatePresence>

        {filteredTurns.length === 0 && (
          <div className="text-center text-xs text-white/40 py-4">
            No {filter === 'me' ? 'your' : "opponent's"} hands yet
          </div>
        )}
      </div>
    </div>
  );
};
